import React, { forwardRef, useEffect, useState } from "react";
import OutsideClickHandler from "react-outside-click-handler";
import FormControlMessage from "../../FormControlMessage";
import FormGroup from "../../FormGroup";
import Label from "../../Label";
import SelectButton from "../SelectButton";
import type { TOptionsList } from "../../../types";
import { getSelectedOptionsTitles } from "../utils";
import withTheme from "../../../hocs/withTheme";
import Loader from "../../Loader";
import MultiSelectMiniForm from "../MultiSelectMiniForm";

type SelectControlsLabel = {
    selectAll: string;
    reset: string;
};

type MultiSelectProps = {
    label?: string | null;
    placeholder?: string;
    withInfobox?: boolean;
    withSelectTool?: boolean;
    required?: boolean;
    options?: TOptionsList | null;
    value?: string[];
    onReset?: () => string[] | void;
    onChange?: (values: string[]) => void;
    onSearch?: (value: string) => void;
    width?: string | number | null;
    error?: string | null;
    errorInTooltip?: boolean;
    disabled?: boolean;
    children?: React.ReactNode;
    selectControlsLabel?: SelectControlsLabel;
    helpText?: boolean | string | null;
    isSearching?: boolean;
    innerRef?: React.Ref<HTMLDivElement>;
    className?: string;
};

const MultiSelect = (props: MultiSelectProps) => {
    const {
        label = null,
        placeholder = " ",
        withInfobox = true,
        withSelectTool = false,
        required = false,
        options = null,
        value = [],
        onReset = () => {},
        onChange = () => {},
        onSearch = () => {},
        width = null,
        error = null,
        errorInTooltip = false,
        disabled = false,
        children = null,
        selectControlsLabel = {
            selectAll: "Select all",
            reset: "Reset",
        },
        helpText = null,
        isSearching = false,
        innerRef: ref = null,
    } = props;
    const [opened, setOpened] = useState(false);
    const [focused, setFocused] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState(value);

    useEffect(() => {
        setSelectedOptions(value);
    }, [value]);

    const handleClickOutside = () => {
        setOpened(false);
    };
    const handleFocus = () => {
        setFocused((val) => !val);
    };
    const handleDropdown = () => {
        setOpened((val) => !val);
    };
    const onSelect = (val: string[]) => {
        setSelectedOptions(val);
        onChange(val);
    };

    return (
        <OutsideClickHandler onOutsideClick={handleClickOutside}>
            <FormGroup
                focused={focused}
                opened={opened}
                disabled={disabled}
                error={!!error}
            >
                {label && (
                    <Label
                        htmlFor="select-dropdown-options"
                        label={label}
                        labelInfobox={withInfobox}
                        required={required}
                        helpText={helpText ?? undefined}
                    />
                )}
                <div
                    className="select select__multi"
                    style={{ width: width ?? undefined }}
                    ref={ref}
                >
                    <SelectButton
                        ariaControlName="multiselectFilter-dropdown"
                        opened={opened}
                        error={error ?? undefined}
                        className={
                            selectedOptions.length === 0
                                ? "SelectButton_with_placeholder"
                                : ""
                        }
                        errorInTooltip={errorInTooltip}
                        selectedOptions={
                            selectedOptions.length > 0
                                ? getSelectedOptionsTitles(
                                      options,
                                      selectedOptions
                                  )
                                : placeholder
                        }
                        onFocus={handleFocus}
                        onBlur={handleFocus}
                        onClick={handleDropdown}
                    />
                    <div className="select__dropdown__wrapper">
                        <MultiSelectMiniForm
                            value={selectedOptions}
                            opened={opened}
                            options={options}
                            headerControls={{ selectControlsLabel }}
                            withSelectTool={withSelectTool}
                            onSearch={onSearch}
                            onSelect={onSelect}
                            onReset={onReset as (() => string[]) | undefined}
                        >
                            {isSearching && <Loader />}
                        </MultiSelectMiniForm>
                    </div>
                </div>

                {error && !errorInTooltip && (
                    <FormControlMessage>{error}</FormControlMessage>
                )}

                {children && <p>{children}</p>}
            </FormGroup>
        </OutsideClickHandler>
    );
};

export default forwardRef<HTMLDivElement, MultiSelectProps>((props, ref) =>
    withTheme(MultiSelect)({ ...props, innerRef: ref })
);
